// AddOvernightTaskDialog.js
// Extracted from AIWorkforcePage.js for better maintainability

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Clock, Plus } from 'lucide-react';
import { PRIORITY_OPTIONS, OVERNIGHT_TASK_TYPES } from '../constants';

/**
 * Dialog for adding a new task to the overnight queue
 * 
 * @param {Object} props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {function} props.onOpenChange - Callback when dialog open state changes
 * @param {Object} props.formData - Current form data { task_type, title, description, custom_input, priority }
 * @param {function} props.onFormChange - Callback when form data changes
 * @param {function} props.onSubmit - Callback when form is submitted
 * @param {function} props.onCancel - Callback when cancel is clicked
 */
export const AddOvernightTaskDialog = ({
  open,
  onOpenChange,
  formData,
  onFormChange,
  onSubmit,
  onCancel
}) => {
  const handleFieldChange = (field, value) => {
    onFormChange({ ...formData, [field]: value });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Add Task to Overnight Queue
          </DialogTitle>
          <DialogDescription>
            Schedule a task for automatic execution after business hours (4:30 PM)
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <Label>Task Type</Label>
            <Select 
              value={formData.task_type || 'ai_execution'} 
              onValueChange={(val) => handleFieldChange('task_type', val)}
            >
              <SelectTrigger data-testid="overnight-task-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OVERNIGHT_TASK_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Task Title *</Label>
            <Input
              value={formData.title || ''}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              placeholder="e.g., Generate Q1 Marketing Report"
              data-testid="overnight-task-title"
            />
          </div>

          <div>
            <Label>Description *</Label>
            <Textarea
              value={formData.description || ''}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              placeholder="Describe what this task should accomplish..."
              rows={3}
              data-testid="overnight-task-description"
            />
          </div>

          <div>
            <Label>Custom Input / Context</Label>
            <Textarea
              value={formData.custom_input || ''}
              onChange={(e) => handleFieldChange('custom_input', e.target.value)}
              placeholder="Additional context or specific instructions for the AI..."
              rows={2}
              data-testid="overnight-task-custom-input"
            />
          </div>

          <div>
            <Label>Priority (1 = Highest, 10 = Lowest)</Label>
            <Select 
              value={String(formData.priority || 5)} 
              onValueChange={(val) => handleFieldChange('priority', parseInt(val))}
            >
              <SelectTrigger data-testid="overnight-task-priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={String(opt.value)}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={onCancel}
            data-testid="overnight-task-cancel"
          >
            Cancel
          </Button>
          <Button 
            onClick={onSubmit}
            disabled={!formData.title || !formData.description}
            data-testid="submit-overnight-task-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add to Queue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddOvernightTaskDialog;
